import { FlexCol, FlexRow } from '@/components/common/Flex';
import { Check } from '@/components/common/icons';
import Icon from '@/components/common/icons/Icon';
import { hoverStyleProps } from '@/components/common/styleHelpers';
import { extractTextFromElement } from '@/util/react';
import { Box, forwardRef, SelectProps, Text, useTheme } from '@chakra-ui/react';
import { assert } from '@common/errors';
import { IconName } from '@common/react/icons';
import pluralize from '@common/util/pluralize';
import { toPx } from '@common/util/styles';
import {
    AsyncCreatableSelect,
    AsyncSelect,
    chakraComponents,
    ChakraStylesConfig,
    GroupBase,
    GroupHeadingProps,
    MenuListProps,
    MenuProps,
    OnChangeValue,
    OptionProps,
    PlaceholderProps,
    Props,
    Select,
    SelectInstance,
    Size,
} from 'chakra-react-select';
import { useMemo } from 'react';
import {
    OptionsOrGroups,
    PropsValue,
} from 'react-select/dist/declarations/src/types';
import { CreatableAdditionalProps } from 'react-select/dist/declarations/src/useCreatable';
import { Button } from './Button';

export type StringOptions = StringOption[] | StringGroupOption[];

export type StringOption<V extends string = string> = {
    value: V;
    label: string | JSX.Element;
};

export type StringGroupOption = {
    label: string;
    parent?: string;
    options: StringOption[];
};

export interface FancySelectOptions<
    IsMulti extends boolean,
    Group extends GroupBase<StringOption>,
> extends CreatableAdditionalProps<StringOption, Group> {
    // Renders a fixed button at the bottom of the menu
    cta?: {
        content: string;
        iconName?: IconName;
        onClick: () => void;
    };
    // Renders a preselect option at the top of the menu for each group, and an "All X" option
    showGroupPreselects?: boolean;
    // If true, clicking on a group's label will select all options in the group
    isGroupHeadingSelectable?: boolean;
    isMulti?: IsMulti;
    isClearable?: boolean;
    menuPlacement?: 'auto' | 'bottom' | 'top';
    maxMenuHeight?: number | 'full';
    menuIsOpen?: boolean;
    menuWidth?: number | string;
    menuShouldBlockScroll?: boolean;
    onMenuOpen?: () => void;
    onMenuClose?: () => void;
    // Sets the top of the menu to the top of the viewport, and the left or right of the menu to the left or right of the select input
    fullScreenPlacement?: 'left' | 'right';
    defaultOptions?: OptionsOrGroups<StringOption, Group> | boolean;
    placeholder?: string | JSX.Element;
    loadOptions?: (
        inputValue: string,
        callback: (options: OptionsOrGroups<StringOption, Group>) => void
    ) => Promise<OptionsOrGroups<StringOption, Group>> | void;
    controlShouldRenderValue?: boolean;
}

interface FancySelectInputProps<
    IsMulti extends boolean,
    Group extends GroupBase<StringOption>,
> extends Omit<
            Props<StringOption, IsMulti, Group>,
            | 'onChange'
            | 'isMulti'
            | 'isClearable'
            | 'menuPlacement'
            | 'maxMenuHeight'
            | 'menuIsOpen'
            | 'menuShouldBlockScroll'
            | 'onMenuOpen'
            | 'onMenuClose'
            | 'size'
            | 'loadOptions'
            | 'defaultOptions'
            | 'variant'
            | 'placeholder'
            | 'controlShouldRenderValue'
        >,
        FancySelectOptions<IsMulti, Group> {
    onChange: (value: OnChangeValue<StringOption, IsMulti>) => void;
    size?: SelectProps['size'];
    variant?: SelectProps['variant'];
}

// A large number like 2000 causes the menu to take up the full screen height from below the control
const MAX_MENU_HEIGHT_PX = 2000;
const PORTAL_Z_INDEX = 1500;

const FancySelectInputInner = <
    IsMulti extends boolean = false,
    Group extends GroupBase<StringOption> = GroupBase<StringOption>,
>(
    {
        size = 'md',
        variant = 'outline',
        fullScreenPlacement,
        maxMenuHeight,
        menuWidth,
        ...props
    }: FancySelectInputProps<IsMulti, Group>,
    ref: React.ForwardedRef<SelectInstance<StringOption, IsMulti, Group>>
) => {
    const options = props.options || [];
    const theme = useTheme();
    const inputStyles = theme.components.Input;

    const selectedOptions =
        props.value &&
        options
            .reduce((acc: StringOption[], optionOrGroup) => {
                if ('value' in optionOrGroup) {
                    acc.push(optionOrGroup);
                } else {
                    acc.push(...optionOrGroup.options);
                }

                return acc;
            }, [])
            .filter((option: StringOption) => {
                if (Array.isArray(props.value)) {
                    return props.value.includes(option.value);
                }
                return option.value === (props.value as string | undefined);
            });

    const chakraStyles: ChakraStylesConfig<StringOption, IsMulti, Group> = {
        clearIndicator: (baseStyles) => ({
            ...baseStyles,
            color: 'gray.400',
            fontSize: '0.5rem',
            h: 5,
            width: 'inherit',
            _hover: {
                color: 'gray.500',
            },
        }),
        dropdownIndicator: (baseStyles, state) => ({
            ...baseStyles,
            display:
                (state.selectProps.isClearable && state.hasValue) ||
                (variant === 'unstyled' && !state.isFocused)
                    ? 'none'
                    : 'flex',
            _groupHover: {
                display:
                    state.selectProps.isClearable && state.hasValue
                        ? 'none'
                        : 'flex',
            },
            color: 'gray.400',
            width: 'inherit',
        }),
        control: (baseStyles, controlProps) => ({
            _focusVisible: {
                borderColor: 'gray.800',
            },
            _disabled: {
                cursor: 'not-allowed',
                opacity: 0.4,
            },
            bg: 'white',
            color: controlProps.menuIsOpen ? 'gray.700' : baseStyles.color,
            display: 'flex',
            justifyContent: 'space-between',
            border: '1px solid',
            borderColor: variant === 'unstyled' ? 'transparent' : 'gray.300',
            borderRadius: 5,
            fontSize: size,
            px: 2.5,
            cursor: 'pointer',
            ...hoverStyleProps({
                borderColor: 'text',
            }),
        }),

        inputContainer: (baseStyles) => ({
            ...baseStyles,
            // Grafts our size-based input styles onto react-select's input,
            // shaving off 2px to account for their border and removing
            // our horizontal padding since theirs is applied to a different
            // component (and would be duplicated).
            ...(inputStyles?.sizes?.[size as Size]?.field
                ? {
                      ...inputStyles?.sizes?.[size as Size]?.field,
                      h: toPx(
                          inputStyles?.sizes?.[size as Size]?.field.h * 4 - 2
                      ),
                      px: 0,
                  }
                : {}),
        }),
        valueContainer: (baseStyles) => ({
            ...baseStyles,
            pt: 0,
            pb: 0,
        }),
        menu: (baseStyles) => ({
            ...baseStyles,
            bg: 'gray.700',
            color: 'white',
            fontSize: 'md',
            p: 1.5,
            my: fullScreenPlacement ? 5 : 2,
            width: menuWidth || 'max-content',
            borderRadius: 5,
            bottomLeftBorderRadius: 5,
            bottomRightBorderRadius: 5,
            position: fullScreenPlacement ? 'relative' : 'absolute',
        }),
        menuList: (baseStyles) => ({
            ...baseStyles,
            bg: 'gray.700',
            border: 'none',
            p: 0,
            borderRadius: 5,
            color: 'white',
            // 100vh - 40px gives 20px above and below the menu (since the menu has a margin-top of 20px)
            maxH: fullScreenPlacement ? 'calc(100vh - 40px)' : baseStyles.maxH,
        }),
        group: (baseStyles) => ({
            ...baseStyles,
            bg: 'gray.700',
            color: 'white',
            borderBottom: '1px solid',
            borderColor: 'gray.800',
            mt: 3,
            pb: 3,
            _last: {
                borderBottom: 'none',
                pb: 0,
            },
        }),
        groupHeading: (baseStyles) => ({
            ...baseStyles,
            bg: 'gray.700',
            ml: props.isMulti ? 5 : 0,
            textTransform: 'uppercase',
            fontSize: 'xs',
            color: 'gray.400',
        }),
        placeholder: (baseStyles) => ({
            ...baseStyles,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }),
        option: (baseStyles, state) => ({
            ...baseStyles,
            borderRadius: 5,
            my: 1,
            mx: 1,
            px: 2,
            py: 2.5,
            width: 'initial',
            cursor: 'pointer',
            color: 'white',
            _active: {
                backgroundColor: 'gray.800',
            },
            ...hoverStyleProps({
                backgroundColor: 'gray.800',
            }),
            bg: state.isFocused ? 'blackAlpha.500' : 'transparent',
        }),
        loadingMessage: (baseStyles) => ({
            ...baseStyles,
            px: 8,
            py: 2,
        }),
        multiValue: (baseStyles) => ({
            ...baseStyles,
            mt: 2,
        }),
    };

    const Component = props.loadOptions
        ? props.onCreateOption
            ? AsyncCreatableSelect
            : AsyncSelect
        : Select;

    return (
        <Component
            {...props}
            maxMenuHeight={
                maxMenuHeight === 'full' ? MAX_MENU_HEIGHT_PX : maxMenuHeight
            }
            ref={ref}
            options={options}
            components={{
                Menu,
                MenuList,
                Option,
                Placeholder,
                GroupHeading,
            }}
            value={
                // When `props.loadOptions` is passed, the options are fetched async, so the above logic to determine
                // selectedOptions won't work, and we must rely on the exact `value` to be passed in
                (props.loadOptions
                    ? props.value
                    : props.isMulti
                      ? selectedOptions
                      : selectedOptions?.at(0)) as PropsValue<StringOption>
            }
            chakraStyles={chakraStyles}
            styles={{
                menuPortal: (baseStyles, portalStyleArgs) => {
                    const offsetPercentage = Math.round(
                        ((baseStyles.top as number) / window.innerHeight) * 100
                    );

                    return {
                        ...baseStyles,
                        zIndex: PORTAL_Z_INDEX,
                        ...(fullScreenPlacement && {
                            top: `${offsetPercentage}%`,
                            transform: `translateY(-${offsetPercentage}%)`,
                            left:
                                portalStyleArgs.rect.left +
                                (fullScreenPlacement === 'right'
                                    ? portalStyleArgs.rect.width + 10
                                    : -(menuWidth ?? 0) - 10),
                        }),
                    };
                },
            }}
            isSearchable
            backspaceRemovesValue={false}
            isClearable={props.isClearable ?? true}
            hideSelectedOptions={false}
            tabSelectsValue={false}
            closeMenuOnSelect={!props.isMulti}
            controlShouldRenderValue={props.controlShouldRenderValue ?? false}
            menuPlacement={props.menuPlacement ?? 'auto'}
            menuPortalTarget={document.body}
            menuShouldBlockScroll={
                // Scrolling feels funky when the menu takes up the full screen, so disabling it when full screen placement is enabled
                !!fullScreenPlacement || props.menuShouldBlockScroll
            }
            menuPosition="fixed"
            noOptionsMessage={() => (
                <Text py={3} px={5}>
                    No matching {pluralize(props.name || 'option', 2, false)}
                </Text>
            )}
            filterOption={(option, inputValue) => {
                // TODO: Consider updating `option` to include a separate icon attribute instead of allowing JSX labels
                const matchesCurrentOption = extractTextFromElement(
                    option.label
                )
                    .toLowerCase()
                    .includes(inputValue.toLowerCase());

                if (matchesCurrentOption) return true;

                // Allow searching by group or parent label
                return options
                    .filter(
                        (group) =>
                            isGroup(group) &&
                            (group.label
                                .toLocaleLowerCase()
                                .includes(inputValue) ||
                                group.parent
                                    ?.toLocaleLowerCase()
                                    .includes(inputValue))
                    )
                    .some(
                        (group) =>
                            isGroup(group) &&
                            group.options.some((o) => o.label === option.label)
                    );
            }}
        />
    );
};

export const FancySelectInput = forwardRef<
    FancySelectInputProps<boolean, GroupBase<any>>,
    'select'
>(FancySelectInputInner);

const GroupHeading = <
    IsMulti extends boolean,
    Group extends GroupBase<StringOption>,
>({
    children,
    ...props
}: GroupHeadingProps<StringOption, IsMulti, Group>) => {
    const selectProps = props.selectProps as FancySelectInputProps<
        IsMulti,
        Group
    >;

    return (
        <chakraComponents.GroupHeading {...props}>
            {selectProps.isGroupHeadingSelectable ? (
                <Box
                    cursor="pointer"
                    onClick={() => {
                        handleGroupClick(
                            props.data.options,
                            selectProps.value,
                            selectProps.onChange
                        );
                    }}
                >
                    {children}
                </Box>
            ) : (
                children
            )}
        </chakraComponents.GroupHeading>
    );
};

// When `controlShouldRenderValue` is set to false, the placeholder is always rendered
const Placeholder = <
    IsMulti extends boolean = false,
    Group extends GroupBase<StringOption> = GroupBase<StringOption>,
>({
    children,
    ...props
}: PlaceholderProps<StringOption, IsMulti, Group>) => {
    const value = props.getValue();
    const count = value.length;

    const options = props.selectProps.options;

    let label: string | JSX.Element =
        count === 1
            ? value[0].label
            : pluralize(props.selectProps.name || 'item', count, true);

    const parentGroupOptions = useMemo(() => {
        if (options.length && isGroup(options[0])) {
            return buildParentGroupOptions(options);
        }
    }, [options]);

    const selectedGroups = (parentGroupOptions ?? options).filter((group) => {
        if ('options' in group) {
            return group.options.every((option) => value.includes(option));
        }

        return false;
    });

    if (selectedGroups.length === 1 && selectedGroups[0].label) {
        label = selectedGroups[0].label;
    }

    return (
        <chakraComponents.Placeholder {...props}>
            {props.selectProps.menuIsOpen &&
            props.selectProps.options.length > 10 ? (
                'Type to search...'
            ) : props.hasValue ? (
                <Text
                    as="span"
                    color="text"
                    overflow="hidden"
                    textOverflow="ellipsis"
                >
                    {label}
                </Text>
            ) : (
                children
            )}
        </chakraComponents.Placeholder>
    );
};

// Override the default Option component to add a checkmark
const Option = <
    IsMulti extends boolean = false,
    Group extends GroupBase<StringOption> = GroupBase<StringOption>,
>({
    children,
    ...props
}: OptionProps<StringOption, IsMulti, Group>) => {
    // Doesn't appear to be documented, but `props.data` has an __isNew__ attribute that gets set when creating a new option
    // A bit hacky, but this seems to be the only way to determine if an option is new
    const isNew = (props.data as Record<string, unknown>)['__isNew__'];
    const showCheckBox = props.isMulti && (!isNew || props.options.length > 1);

    return (
        <chakraComponents.Option {...props}>
            <FlexRow align="center" data-cy="fancy-select-option">
                {showCheckBox && (
                    <Box mr={2.5} w={3} mb={0.5}>
                        {props.isSelected && (
                            <Check color="white" size="small" />
                        )}
                    </Box>
                )}
                {children}
            </FlexRow>
        </chakraComponents.Option>
    );
};

// Override the default Menu component to conditionally show a CTA button at the bottom of the menu
const Menu = <IsMulti extends boolean, Group extends GroupBase<StringOption>>({
    children,
    ...props
}: MenuProps<StringOption, IsMulti, Group>) => {
    const selectProps = props.selectProps as FancySelectInputProps<
        IsMulti,
        Group
    >;
    return (
        <chakraComponents.Menu {...props}>
            {children}
            {selectProps.cta && (
                <Button
                    position="absolute"
                    bottom={0}
                    left={0}
                    right={0}
                    zIndex={PORTAL_Z_INDEX + 1}
                    borderTopColor="gray.500"
                    borderTopWidth="1px"
                    borderTopRightRadius={0}
                    borderTopLeftRadius={0}
                    pl={6}
                    backgroundColor="black"
                    color="gray.400"
                    fontWeight="normal"
                    justifyContent="space-between"
                    {...hoverStyleProps({
                        bg: 'gray.800',
                        color: 'white',
                    })}
                    _active={{ bg: 'gray.800' }}
                    onClick={selectProps.cta?.onClick}
                    onClickEvent="Select Menu Button Clicked"
                    onClickEventParams={{
                        text: selectProps.cta.content,
                        name: selectProps.name,
                    }}
                >
                    {selectProps.cta.content}
                    {selectProps.cta.iconName && (
                        <Icon name={selectProps.cta.iconName} />
                    )}
                </Button>
            )}
        </chakraComponents.Menu>
    );
};

// Override the default MenuList component to conditionally add a preselect option at the top of the menu for each group,
const MenuList = <
    IsMulti extends boolean,
    Group extends GroupBase<StringOption>,
>({
    children,
    ...props
}: MenuListProps<StringOption, IsMulti, Group>) => {
    const { getValue, clearValue, hasValue } = props;
    const selectProps = props.selectProps as FancySelectInputProps<
        IsMulti,
        Group
    >;

    const options = selectProps.options;
    assert(options, 'FancySelectInput missing options prop');

    const value = getValue();
    const parentGroupOptions = useMemo(() => {
        if (
            selectProps.showGroupPreselects &&
            options.length &&
            isGroup(options[0])
        ) {
            return buildParentGroupOptions(options);
        }
    }, [selectProps.showGroupPreselects, options]);

    const preselects = selectProps.showGroupPreselects
        ? (parentGroupOptions ?? options ?? []).map(
              (group) =>
                  isGroup(group) &&
                  // Only show preselects that match the input value, or show all of them if there's no input value
                  (!selectProps.inputValue ||
                      group.label
                          .toLowerCase()
                          .includes(selectProps.inputValue.toLowerCase())) && (
                      <Preselect
                          key={group.label}
                          label={group.label}
                          selected={
                              value.length > 0 &&
                              value.length >= group.options.length &&
                              group.options.every((o) => value.includes(o))
                          }
                          onClick={() => {
                              handleGroupClick(
                                  group.options,
                                  selectProps.value,
                                  selectProps.onChange
                              );
                          }}
                      />
                  )
          )
        : [];

    return (
        <chakraComponents.MenuList {...props}>
            {selectProps.showGroupPreselects && (
                <FlexCol py={2} borderBottom="1px solid" borderColor="gray.800">
                    <Preselect
                        label={`All ${pluralize(
                            selectProps.name || 'item',
                            2,
                            false
                        )}`}
                        selected={!hasValue}
                        onClick={() => {
                            clearValue();
                        }}
                    />
                    {preselects}
                </FlexCol>
            )}
            {/* Since the CTA is positioned absolutely, need to add extra margin to the bottom of the menu list so that last option isn't blocked */}
            <Box mb={selectProps.cta ? 15 : 0}>{children}</Box>
        </chakraComponents.MenuList>
    );
};

const Preselect = ({
    label,
    selected,
    onClick,
}: {
    label: string;
    selected: boolean;
    onClick: () => void;
}) => (
    <FlexRow
        p={2.5}
        mx={1}
        borderRadius={5}
        {...hoverStyleProps({
            bg: 'blackAlpha.500',
        })}
        _active={{ bg: 'gray.800' }}
        onClick={onClick}
        align="center"
    >
        <Box mr={2.5} w={3}>
            {selected && <Check color="white" size="small" />}
        </Box>
        {label}
    </FlexRow>
);

const buildParentGroupOptions = <Group extends GroupBase<StringOption>>(
    options: OptionsOrGroups<StringOption, Group>
) => {
    if ('parent' in options[0] && options[0].parent) {
        const map: Record<string, StringGroupOption> = {};

        options.forEach((option) => {
            if (isGroup(option) && option.parent) {
                if (map[option.parent]) {
                    map[option.parent].options = [
                        ...map[option.parent].options,
                        ...option.options,
                    ];
                } else {
                    map[option.parent] = {
                        label: option.parent,
                        options: option.options,
                    };
                }
            }
        });

        return Object.values(map);
    }
};

const handleGroupClick = <IsMulti extends boolean>(
    options: StringOption[] | readonly StringOption[],
    value: PropsValue<StringOption> | undefined,
    onChange: (value: OnChangeValue<StringOption, IsMulti>) => void
) => {
    if (!value) {
        // No selected values; select all options in group
        onChange(options as OnChangeValue<StringOption, IsMulti>);
    } else if (Array.isArray(value)) {
        let newValue: readonly StringOption[];

        if (value.some((v) => options.includes(v))) {
            // Some selected values are in group; remove them from selected options
            newValue = value.filter((v) => !options.includes(v));
        } else {
            // No selected values are in group; add them to selected options
            newValue = [...value, ...options];
        }

        onChange(newValue as OnChangeValue<StringOption, IsMulti>);
    }
};

const isGroup = (item: any): item is StringGroupOption => {
    return 'options' in item;
};
